"use client";

import { motion } from "framer-motion";
import {
  CheckCircle2,
  CreditCard,
  AlertTriangle,
  Zap,
  MessageCircle,
  Mail,
  Package,
  Trophy,
  Camera,
  Search,
  Paperclip,
  Send,
  Phone,
  MoreHorizontal,
  ArrowUpRight,
  Filter,
  Download,
  RefreshCw,
  Users,
  UserCheck,
  TrendingUp,
  Eye,
  ShoppingBag,
  QrCode,
  Globe,
  Banknote,
  Moon,
  XCircle,
  Sparkles,
  Heart,
  Truck,
  Clock,
  ShieldAlert,
  Receipt,
  PackageSearch,
  CalendarDays,
  ShoppingCart,
  DollarSign,
  Activity,
  RefreshCcw,
  BarChart3,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// Shared helpers
// ─────────────────────────────────────────────────────────────────────────────

function MulplyLogo() {
  return (
    <>
      <img src="/logotipo_gradient.svg" alt="Mulply" className="h-5 w-auto dark:hidden flex-shrink-0" />
      <img src="/logotipo_white.svg" alt="Mulply" className="h-5 w-auto hidden dark:block flex-shrink-0" />
    </>
  );
}

function BrowserShell({ url, children }: { url: string; children: React.ReactNode }) {
  return (
    <div className="w-full rounded-2xl overflow-hidden border border-gray-200/60 dark:border-white/10 shadow-[0_40px_120px_-12px_rgba(0,0,0,0.20)] dark:shadow-[0_40px_120px_-12px_rgba(0,0,0,0.70)]">
      <div className="flex items-center gap-3 px-4 h-10 bg-gray-100 dark:bg-[#1c1c28] border-b border-gray-200/60 dark:border-white/[0.07] flex-shrink-0">
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
          <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
          <span className="w-3 h-3 rounded-full bg-[#28c840]" />
        </div>
        <div className="flex-1 flex justify-center">
          <div className="flex items-center gap-2 px-3 h-6 rounded bg-white/80 dark:bg-white/10 border border-gray-200/50 dark:border-white/10 min-w-[200px] justify-center">
            <span className="w-2 h-2 rounded-full bg-[#48c1ce]/80 flex-shrink-0" />
            <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 select-none truncate">{url}</span>
          </div>
        </div>
        <div className="w-16" />
      </div>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Mock 0 · Tracking (página /dashboard/tracking)
// ─────────────────────────────────────────────────────────────────────────────

const TRACKING_STATUS_CFG = {
  transit: { label: "Em Trânsito", icon: Truck, cls: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20" },
  delivered: { label: "Entregue", icon: CheckCircle2, cls: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20" },
  pending: { label: "Pendente", icon: Clock, cls: "bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20" },
  risk: { label: "SLA Violado", icon: AlertTriangle, cls: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20" },
  customs: { label: "Fiscalização", icon: ShieldAlert, cls: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20" },
  tax: { label: "Taxado", icon: Receipt, cls: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20" },
  out: { label: "Saiu p/ Entrega", icon: Truck, cls: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20" },
} as const;

type TStatus = keyof typeof TRACKING_STATUS_CFG;

function TrackingStatusBadge({ status }: { status: TStatus }) {
  const { label, icon: Icon, cls } = TRACKING_STATUS_CFG[status];
  return (
    <div className={`inline-flex items-center gap-1.5 rounded-md border text-[9px] font-semibold uppercase tracking-[0.1em] px-2 py-1 whitespace-nowrap ${cls}`}>
      <Icon className="w-3 h-3 flex-shrink-0" />
      {label}
    </div>
  );
}

const TRACKING_ROWS: {
  id: string; tracking: string; date: string; posted: string;
  status: TStatus; sla: string | null; slaAmber: boolean; slaRed: boolean;
}[] = [
    { id: "#10042", tracking: "TJ042123456BR", date: "14/04/26", posted: "12/04/26", status: "transit", sla: "2 dias", slaAmber: false, slaRed: false },
    { id: "#10041", tracking: "TJ041987654BR", date: "13/04/26", posted: "11/04/26", status: "delivered", sla: null, slaAmber: false, slaRed: false },
    { id: "#10040", tracking: "—", date: "13/04/26", posted: "—", status: "pending", sla: "Hoje", slaAmber: false, slaRed: false },
    { id: "#10039", tracking: "TJ039654321BR", date: "12/04/26", posted: "07/04/26", status: "risk", sla: "15 dias sem atualização", slaAmber: false, slaRed: true },
    { id: "#10038", tracking: "TJ038111222BR", date: "11/04/26", posted: "06/04/26", status: "customs", sla: "8 dias sem atualização", slaAmber: true, slaRed: false },
    { id: "#10037", tracking: "TJ037333444BR", date: "10/04/26", posted: "05/04/26", status: "tax", sla: "12 dias sem atualização", slaAmber: false, slaRed: true },
  ];

const STATUS_TABS = [
  { icon: PackageSearch, label: "Todos", count: "142", active: false },
  { icon: Clock, label: "Pendentes", count: "18", active: false },
  { icon: Truck, label: "Em Trânsito", count: "23", active: true },
  { icon: ShieldAlert, label: "Fiscal.", count: "4", active: false },
  { icon: Receipt, label: "Taxados", count: "7", active: false },
  { icon: CheckCircle2, label: "Entregues", count: "85", active: false },
  { icon: AlertTriangle, label: "SLA Violado", count: "5", active: false },
];

function TrackingMock() {
  return (
    <BrowserShell url="mulply.com/tracking">
      <div className="bg-white dark:bg-[#0d0d1a] flex flex-col">
        {/* App header */}
        <div className="flex items-center justify-between px-5 py-2.5 border-b border-gray-100 dark:border-white/[0.07] bg-gray-50/40 dark:bg-white/[0.02]">
          <div className="flex items-center gap-3">
            <MulplyLogo />
            <div className="flex items-center gap-2">
              <PackageSearch className="w-3.5 h-3.5 text-[#48c1ce]" />
              <span className="text-xs font-black text-gray-700 dark:text-gray-100">Rastreio e SAC</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-white/15 text-[9px] font-bold text-gray-500 dark:text-gray-400 bg-transparent whitespace-nowrap">
              <CalendarDays className="w-2.5 h-2.5" />
              Abr 2026
            </button>
            <button className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-white/15 text-[9px] font-bold text-gray-500 dark:text-gray-400 bg-transparent">
              <Download className="w-2.5 h-2.5" />
              CSV
            </button>
          </div>
        </div>

        {/* Status tabs */}
        <div className="flex items-end gap-0 border-b border-gray-100 dark:border-white/[0.07] bg-gray-50/20 dark:bg-white/[0.01] overflow-x-auto scrollbar-hide">
          {STATUS_TABS.map(({ icon: Icon, label, count, active }) => (
            <div key={label} className={`relative flex items-center gap-1.5 px-3.5 py-2.5 cursor-pointer whitespace-nowrap select-none flex-shrink-0 ${active ? "text-[#48c1ce]" : "text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"}`}>
              <Icon className="w-3 h-3" />
              <span className="text-[10px] font-bold">{label}</span>
              <span className={`px-1.5 py-0.5 rounded-full text-[8px] font-black ${active ? "bg-[#48c1ce]/15 text-[#48c1ce]" : "bg-gray-100 dark:bg-white/10 text-gray-400 dark:text-gray-500"}`}>{count}</span>
              {active && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#48c1ce]" />}
            </div>
          ))}
        </div>

        {/* Search bar */}
        <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-100 dark:border-white/[0.07]">
          <div className="relative flex-1">
            <Search className="w-3 h-3 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <div className="w-full pl-7 pr-3 py-1.5 rounded-lg bg-gray-100 dark:bg-white/10 text-[10px] text-gray-400 font-medium select-none">
              Buscar pedido ou rastreio…
            </div>
          </div>
          <button className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-white/15 text-[9px] font-bold text-gray-500 dark:text-gray-400">
            <Filter className="w-2.5 h-2.5" /> Filtro
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse">
            <thead>
              <tr className="border-b border-gray-100 dark:border-white/[0.07] bg-gray-50 dark:bg-white/[0.025]">
                {/* Espelha o cabeçalho real da tela de Rastreio (`tracking/columns.tsx`). */}
                {["PEDIDO", "RASTREIO", "DATA DO PEDIDO", "POSTAGEM", "STATUS LOGÍSTICO", "SEM MOVIMENTO", ""].map((h) => (
                  <th key={h} className="px-4 py-2 text-left text-[9px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 whitespace-nowrap first:pl-5 last:pr-5">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {TRACKING_ROWS.map((row) => (
                <tr
                  key={row.id}
                  className={`border-b last:border-0 transition-colors ${row.slaRed
                    ? "border-red-200/60 dark:border-red-500/20 bg-red-50/50 dark:bg-red-500/[0.06]"
                    : "border-gray-50 dark:border-white/[0.04] hover:bg-gray-50/50 dark:hover:bg-white/[0.025]"
                    }`}
                  style={row.slaRed ? { borderLeft: "2px solid #ef4444" } : {}}
                >
                  <td className="px-4 py-3 pl-5">
                    <span className="font-semibold text-sm text-gray-800 dark:text-gray-100 tabular-nums">{row.id}</span>
                  </td>
                  <td className="px-4 py-3">
                    {row.tracking === "—" ? (
                      <span className="text-[9px] font-semibold text-gray-300 dark:text-gray-600 italic">Sem Rastreio</span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 dark:bg-white/10 text-[9px] font-black text-gray-600 dark:text-gray-300 tabular-nums tracking-wide">
                        {row.tracking}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 tabular-nums">{row.date}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 tabular-nums">{row.posted}</span>
                  </td>
                  <td className="px-4 py-3">
                    <TrackingStatusBadge status={row.status} />
                  </td>
                  <td className="px-4 py-3">
                    {row.sla === null ? (
                      <span className="text-[9px] text-gray-300 dark:text-gray-600">—</span>
                    ) : (
                      <div className="flex flex-col gap-0.5">
                        <span className={`text-[10px] font-semibold tabular-nums whitespace-nowrap ${row.slaRed ? "text-red-600 dark:text-red-400" : row.slaAmber ? "text-amber-600 dark:text-amber-400" : "text-gray-500 dark:text-gray-400"
                          }`}>{row.sla}</span>
                        {row.slaRed && (
                          <span className="text-[8px] font-black text-red-500 uppercase tracking-wide">SLA Violado</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 pr-5">
                    <button className="w-6 h-6 rounded-md bg-gray-100 dark:bg-white/10 flex items-center justify-center">
                      <MoreHorizontal className="w-3 h-3 text-gray-400" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-5 py-2.5 border-t border-gray-100 dark:border-white/[0.07] bg-gray-50/40 dark:bg-white/[0.02]">
          <span className="text-[9px] text-gray-400 dark:text-gray-500 font-semibold">Mostrando 6 de 23 em trânsito · Atualizado há 2 min</span>
          <span className="flex items-center gap-1 text-[#48c1ce] text-[9px] font-black cursor-pointer">Ver todos <ArrowUpRight className="w-3 h-3" /></span>
        </div>
      </div>
    </BrowserShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Mock Dashboard (página /dashboard — réplica fiel do sistema real)
// ─────────────────────────────────────────────────────────────────────────────

const CURR_CARDS = [
  { currency: "USD", value: "R$5,12", change: "+0,3%", up: true  },
  { currency: "EUR", value: "R$5,89", change: "0%",    up: null  },
  { currency: "GBP", value: "R$6,78", change: "-0,1%", up: false },
  { currency: "MXN", value: "R$0,31", change: "+0,5%", up: true  },
];

const DASH_KPIS = [
  { label: "Vendas (Criados)", value: "R$1.362.867,44", sub: "Volume total de pedidos criados", icon: ShoppingCart, ibg: "bg-gray-100 dark:bg-white/10", ic: "text-gray-500 dark:text-gray-400" },
  { label: "Receita (Paga)",   value: "R$1.034.339,97", sub: "Faturamento real processado",     icon: DollarSign,   ibg: "bg-emerald-500/10",             ic: "text-emerald-600"                 },
  { label: "Ticket Médio",     value: "R$219,53",        sub: "Média por pedido pago",           icon: Activity,     ibg: "bg-gray-100 dark:bg-white/10", ic: "text-gray-500 dark:text-gray-400" },
  { label: "LTV Médio",        value: "R$186,20",        sub: "Receita média por cliente",       icon: Users,        ibg: "bg-[#48c1ce]/10",               ic: "text-[#48c1ce]"                   },
  { label: "Taxa Cancel.",     value: "5,2%",             sub: "Sobre total de criados",          icon: RefreshCcw,   ibg: "bg-red-500/10",                 ic: "text-red-500"                     },
  { label: "Pendentes",        value: "0",                sub: "Pagos e aguardando envio",        icon: Truck,        ibg: "bg-amber-500/10",               ic: "text-amber-500"                   },
];

// jan=200k fev=245k mar=330k abr=260k  (max≈330k → 100%)
const MONTH_BARS = [
  { month: "jan", total: 60, indigo: 34, green: 19, orange: 7 },
  { month: "fev", total: 74, indigo: 42, green: 25, orange: 7 },
  { month: "mar", total: 100, indigo: 56, green: 36, orange: 8 },
  { month: "abr", total: 79, indigo: 44, green: 28, orange: 7 },
];
const Y_LABELS = ["R$340.000", "R$255.000", "R$170.000", "R$85.000", "R$0"];

function DonutChart({ segs, total, center }: { segs: { v: number; c: string }[]; total: number; center: string }) {
  const r = 34;
  const circ = 2 * Math.PI * r;
  let cumul = 0;
  return (
    <div className="relative w-20 h-20 flex-shrink-0">
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <circle cx="50" cy="50" r={r} fill="none" strokeWidth="13" className="stroke-gray-100 dark:stroke-white/10" />
        {segs.map((s, i) => {
          const dash = (s.v / total) * circ;
          const rot = (cumul / total) * 360 - 90;
          cumul += s.v;
          return <circle key={i} cx="50" cy="50" r={r} fill="none" strokeWidth="13" stroke={s.c} strokeDasharray={`${dash} ${circ - dash}`} transform={`rotate(${rot} 50 50)`} />;
        })}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xs font-black text-gray-800 dark:text-gray-100 tabular-nums">{center}</span>
      </div>
    </div>
  );
}

const VIP_CLIENTS = [
  { ini: "BR", name: "Bruno Alves",   email: "brunoalves@gmail.com",  cc: "br", value: "R$2.255,75", seg: "VIP ATIVO",  segCls: "text-amber-600 bg-amber-500/10"   },
  { ini: "MC", name: "Mariana Costa", email: "mariana.c@hotmail.com", cc: "br", value: "R$2.079,00", seg: "VIP ATIVO",  segCls: "text-amber-600 bg-amber-500/10"   },
  { ini: "FR", name: "Felipe Ramos",  email: "felipew@gmail.com",     cc: "br", value: "R$1.464,71", seg: "PROMISSOR",  segCls: "text-emerald-600 bg-emerald-500/10"},
  { ini: "TN", name: "Tatiana Nunes", email: "t.nunes@live.com",      cc: "mx", value: "R$1.050,04", seg: "VIP ATIVO",  segCls: "text-amber-600 bg-amber-500/10"   },
  { ini: "IB", name: "Igor Bezerra",  email: "igor.b@email.com",      cc: "br", value: "R$1.044,52", seg: "PROMISSOR",  segCls: "text-emerald-600 bg-emerald-500/10"},
];

const TOP_PRODS = [
  { rank: 1, name: "Kit Fitness Premium Bolsa G",      badge: "EM ALTA", sales: 1840 },
  { rank: 2, name: "Camiseta Dry-Fit Pack 3un",         badge: "EM ALTA", sales: 1404 },
  { rank: 3, name: "Tênis Runner Carbon Masculino",     badge: "EM ALTA", sales: 627  },
  { rank: 4, name: "Suplemento Whey Gold 1kg",          badge: "",        sales: 345  },
  { rank: 5, name: "Mochila Ultralight 30L Cinza",      badge: "",        sales: 298  },
];

const D_AVATAR = ["bg-[#ec399a]/20 text-[#ec399a]","bg-[#48c1ce]/20 text-[#48c1ce]","bg-amber-500/20 text-amber-600","bg-purple-500/20 text-purple-600","bg-emerald-500/20 text-emerald-600"];

export function DashboardMock() {
  return (
    <BrowserShell url="app.mulply.com/dashboard">
      <div className="bg-white dark:bg-[#0d0d1a] flex flex-col text-gray-800 dark:text-gray-100">

        {/* Currency row */}
        <div className="grid grid-cols-4 gap-px bg-gray-100 dark:bg-white/[0.04] border-b border-gray-100 dark:border-white/[0.07]">
          {CURR_CARDS.map(({ currency, value, change, up }) => (
            <div key={currency} className="bg-white dark:bg-[#0d0d1a] px-4 py-1 flex flex-col gap-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <DollarSign className="w-3 h-3 text-[#48c1ce]" />
                  <span className="text-[8px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">{currency}</span>
                </div>
                <span className={`text-[8px] font-bold ${up === true ? "text-emerald-500" : up === false ? "text-red-500" : "text-gray-400"}`}>{change}</span>
              </div>
              <span className="text-xs font-black tabular-nums text-gray-800 dark:text-gray-100 leading-tight">{value}</span>
            </div>
          ))}
        </div>

        {/* Page header */}
        <div className="flex items-center justify-between px-5 py-1.5 border-b border-gray-100 dark:border-white/[0.07]">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-[#48c1ce]" />
            <div>
              <div className="text-xs font-black text-gray-900 dark:text-white leading-tight">Dashboard</div>
              <div className="text-[7px] text-gray-400 dark:text-gray-500">Visão completa das suas operações em tempo real</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button className="flex items-center gap-1 px-2 py-0.5 rounded-md border border-gray-200 dark:border-white/10 text-[8px] font-semibold text-gray-500 dark:text-gray-400">
              <Filter className="w-2.5 h-2.5" /> Configurar Moedas
            </button>
            <button className="flex items-center gap-1 px-2 py-0.5 rounded-md border border-gray-200 dark:border-white/10 text-[8px] font-semibold text-gray-500 dark:text-gray-400">
              <RefreshCw className="w-2.5 h-2.5" /> Atualizar
            </button>
          </div>
        </div>

        {/* Tab + period bar */}
        <div className="flex items-center justify-between px-4 py-1 bg-gray-50/60 dark:bg-white/[0.02] border-b border-gray-100 dark:border-white/[0.07] gap-2 flex-wrap">
          <div className="flex items-center gap-0.5 bg-gray-100 dark:bg-white/[0.07] rounded-xl p-0.5">
            {["Geral", "Yampi", "Shopify"].map((tab, i) => (
              <div key={tab} className={`px-3 py-0.5 rounded-lg text-[9px] font-black cursor-pointer ${i === 0 ? "bg-white dark:bg-white/15 text-[#48c1ce] shadow-sm" : "text-gray-400 dark:text-gray-500"}`}>{tab}</div>
            ))}
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            <div className="flex items-center gap-0.5 bg-gray-100 dark:bg-white/[0.07] rounded-xl p-0.5">
              {["HOJE","ONTEM","SEMANA","MÊS","ANO"].map((p, i) => (
                <div key={p} className={`px-2 py-0.5 rounded-lg text-[8px] font-black cursor-pointer ${i === 4 ? "bg-[#48c1ce] text-white shadow-sm" : "text-gray-400 dark:text-gray-500"}`}>{p}</div>
              ))}
            </div>
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-lg border border-gray-200 dark:border-white/10 text-[8px] font-semibold text-gray-400">
              <CalendarDays className="w-2.5 h-2.5" /> 01/01/2026 – 19/04/2026
            </div>
            <button className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-[#48c1ce]/10 text-[#48c1ce] text-[8px] font-black border border-[#48c1ce]/20">
              <Zap className="w-2.5 h-2.5" /> SINCRONIZAR TODAS
            </button>
            <button className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-amber-500/10 text-amber-600 text-[8px] font-black border border-amber-500/20">
              <Clock className="w-2.5 h-2.5" /> HISTÓRICO 2025
            </button>
          </div>
        </div>

        {/* 6 KPI cards */}
        <div className="grid grid-cols-6 gap-px bg-gray-100/60 dark:bg-white/[0.04] border-b border-gray-100 dark:border-white/[0.07]">
          {DASH_KPIS.map(({ label, value, sub, icon: Icon, ibg, ic }) => (
            <div key={label} className="bg-white dark:bg-[#0d0d1a] px-3 py-2 flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <span className="text-[7px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 leading-tight">{label}</span>
                <div className={`p-1 rounded-md ${ibg}`}><Icon className={`w-2.5 h-2.5 ${ic}`} /></div>
              </div>
              <span className="text-xs font-black tabular-nums text-gray-900 dark:text-white leading-none">{value}</span>
              <span className="text-[7px] text-gray-400 dark:text-gray-500 uppercase tracking-wide leading-tight truncate">{sub}</span>
            </div>
          ))}
        </div>

        {/* Evolução de Receita + Funil */}
        <div className="grid grid-cols-[2fr_1fr] gap-px bg-gray-100/60 dark:bg-white/[0.04] border-b border-gray-100 dark:border-white/[0.07]">
          <div className="bg-white dark:bg-[#0d0d1a] px-3 py-2 flex flex-col gap-1.5">
            <div>
              <div className="text-[10px] font-black text-gray-700 dark:text-gray-200">Evolução de Receita</div>
              <div className="text-[8px] text-gray-400">Receita paga diária no período</div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {[["Geral","#6366f1"],["Yampi","#48c1ce"],["Shopify","#ec399a"]].map(([n, c]) => (
                <div key={n} className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: c as string }} />
                  <span className="text-[7px] text-gray-400">{n}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <div className="flex flex-col justify-between flex-shrink-0" style={{ height: "62px" }}>
                {Y_LABELS.map((l) => (
                  <span key={l} className="text-[7px] text-[#48c1ce] font-semibold tabular-nums leading-none">{l}</span>
                ))}
              </div>
              <div className="flex-1 flex flex-col gap-1">
                <div className="relative" style={{ height: "62px" }}>
                  {[0, 25, 50, 75, 100].map((pct) => (
                    <div key={pct} className="absolute left-0 right-0 border-t border-dashed border-gray-100 dark:border-white/[0.05]" style={{ bottom: `${pct}%` }} />
                  ))}
                  <div className="absolute inset-0 flex items-end gap-4">
                    {MONTH_BARS.map(({ month, total, indigo, green, orange }) => {
                      const maxPx = 60;
                      const barPx = Math.round((total / 100) * maxPx);
                      const iPx = Math.round((indigo / total) * barPx);
                      const gPx = Math.round((green / total) * barPx);
                      const oPx = barPx - iPx - gPx;
                      return (
                        <div key={month} className="flex-1 h-full flex flex-col justify-end items-center">
                          <div className="w-[66%] rounded-t overflow-hidden" style={{ height: `${barPx}px` }}>
                            <div style={{ height: `${iPx}px`, background: "#6366f1" }} />
                            <div style={{ height: `${gPx}px`, background: "#48c1ce" }} />
                            <div style={{ height: `${oPx}px`, background: "#f59e0b" }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="flex gap-4">
                  {MONTH_BARS.map(({ month }) => (
                    <div key={month} className="flex-1 text-center">
                      <span className="text-[8px] text-gray-400">{month}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-[#0d0d1a] px-3 py-2 flex flex-col gap-2">
            <div>
              <div className="text-[10px] font-black text-gray-700 dark:text-gray-200">Funil de Conversão</div>
              <div className="text-[8px] text-gray-400">Performance de Fechamento</div>
            </div>
            <div className="flex flex-col gap-2">
              <div>
                <div className="flex items-center justify-between">
                  <span className="text-[8px] font-black uppercase tracking-widest text-gray-400">CRIADOS</span>
                  <span className="text-xs font-black tabular-nums text-gray-800 dark:text-gray-100">6.208</span>
                </div>
                <div className="text-[8px] text-gray-400">Volume Bruto</div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <span className="text-[8px] font-black uppercase tracking-widest text-[#48c1ce]">CONFIRMADOS</span>
                  <span className="text-xs font-black tabular-nums text-[#48c1ce]">5.724</span>
                </div>
                <div className="w-full h-1.5 bg-gray-100 dark:bg-white/10 rounded-full overflow-hidden my-0.5">
                  <div className="h-full rounded-full bg-emerald-500" style={{ width: "92.2%" }} />
                </div>
                <div className="text-[8px] text-gray-400">Pagos</div>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-[8px] font-black uppercase tracking-widest text-gray-400">TAXA DE CONVERSÃO</span>
                <span className="text-xl font-black bg-gradient-to-r from-[#48c1ce] to-[#ec399a] bg-clip-text text-transparent">92.2%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Países + Pagamentos + Parcelamentos */}
        <div className="grid grid-cols-3 gap-px bg-gray-100/60 dark:bg-white/[0.04] border-b border-gray-100 dark:border-white/[0.07]">
          <div className="bg-white dark:bg-[#0d0d1a] px-3 py-2 flex flex-col gap-1.5">
            <div className="text-[10px] font-black text-gray-700 dark:text-gray-200">Top 5 Países</div>
            {[{ cc:"es",pct:92 },{ cc:"br",pct:87 },{ cc:"mx",pct:34 },{ cc:"gb",pct:18 },{ cc:"us",pct:14 }].map(({ cc, pct }) => (
              <div key={cc} className="flex items-center gap-2">
                <img src={`https://flagcdn.com/w20/${cc}.png`} width="12" alt={cc} className="rounded-[1.5px] shadow-[0_0_0_1px_rgba(0,0,0,0.1)] flex-shrink-0" />
                <span className="text-[8px] font-semibold text-gray-400 uppercase w-4">{cc}</span>
                <div className="flex-1 h-1.5 bg-gray-100 dark:bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-[#6366f1]" style={{ width: `${pct}%` }} />
                </div>
              </div>
            ))}
          </div>

          {/* Formas de Pagamento */}
          <div className="bg-white dark:bg-[#0d0d1a] flex flex-col">
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-100 dark:border-white/[0.07]">
              <span className="text-[10px] font-black text-gray-700 dark:text-gray-200">Formas de Pagamento</span>
              <ArrowUpRight className="w-3 h-3 text-gray-300 dark:text-gray-600" />
            </div>
            <div className="flex justify-center py-2">
              <DonutChart segs={[{v:2531,c:"#6366f1"},{v:1470,c:"#48c1ce"},{v:1430,c:"#ec399a"},{v:638,c:"#f59e0b"}]} total={6069} center="6208" />
            </div>
            <div className="flex items-center gap-2 px-3 pb-1 border-b border-gray-100 dark:border-white/[0.07]">
              <span className="text-[7px] font-black uppercase tracking-widest text-gray-400 flex-1">CATEGORIA</span>
              <span className="text-[7px] font-black uppercase tracking-widest text-gray-400">QTD / SHARE</span>
            </div>
            <div className="divide-y divide-gray-50 dark:divide-white/[0.04]">
              {([["shopify_pay","#6366f1","2531","49.8%"],["Cartão","#48c1ce","1470","23.7%"],["Pix","#ec399a","1430","23.0%"],["Paypal","#f59e0b","638","10.3%"]] as [string,string,string,string][]).map(([n,c,q,p]) => (
                <div key={n} className="flex items-center gap-2 px-3 py-1 hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors cursor-pointer group">
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: c }} />
                  <span className="text-[9px] font-semibold text-gray-600 dark:text-gray-400 flex-1 truncate group-hover:text-gray-800 dark:group-hover:text-gray-200 transition-colors">{n}</span>
                  <span className="text-[9px] font-black text-gray-700 dark:text-gray-300 tabular-nums">{q}</span>
                  <span className="text-[9px] text-gray-400 tabular-nums w-8 text-right">{p}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Parcelamentos (Pagos) */}
          <div className="bg-white dark:bg-[#0d0d1a] flex flex-col">
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-100 dark:border-white/[0.07]">
              <span className="text-[10px] font-black text-gray-700 dark:text-gray-200">Parcelamentos (Pagos)</span>
              <ArrowUpRight className="w-3 h-3 text-gray-300 dark:text-gray-600" />
            </div>
            <div className="flex justify-center py-2">
              <DonutChart segs={[{v:3329,c:"#6366f1"},{v:194,c:"#48c1ce"},{v:1005,c:"#ec399a"},{v:9,c:"#f59e0b"}]} total={4537} center="4635" />
            </div>
            <div className="flex items-center gap-2 px-3 pb-1 border-b border-gray-100 dark:border-white/[0.07]">
              <span className="text-[7px] font-black uppercase tracking-widest text-gray-400 flex-1">CATEGORIA</span>
              <span className="text-[7px] font-black uppercase tracking-widest text-gray-400">QTD / SHARE</span>
            </div>
            <div className="divide-y divide-gray-50 dark:divide-white/[0.04]">
              {([["1x","#6366f1","3.329","71.8%"],["2x","#48c1ce","194","4.2%"],["3x","#ec399a","1.005","21.7%"],["4x","#f59e0b","9","0.2%"]] as [string,string,string,string][]).map(([n,c,q,p]) => (
                <div key={n} className="flex items-center gap-2 px-3 py-1 hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors cursor-pointer group">
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: c }} />
                  <span className="text-[9px] font-semibold text-gray-600 dark:text-gray-400 flex-1 group-hover:text-gray-800 dark:group-hover:text-gray-200 transition-colors">{n}</span>
                  <span className="text-[9px] font-black text-gray-700 dark:text-gray-300 tabular-nums">{q}</span>
                  <span className="text-[9px] text-gray-400 tabular-nums w-8 text-right">{p}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* VIP Clients + Top Products */}
        <div className="grid grid-cols-2 gap-px bg-gray-100/60 dark:bg-white/[0.04]">
          <div className="bg-white dark:bg-[#0d0d1a] flex flex-col">
            <div className="flex items-center justify-between px-4 py-1.5 border-b border-gray-100 dark:border-white/[0.07]">
              <span className="text-[10px] font-black text-gray-700 dark:text-gray-200">Top Clientes VIP</span>
              <ArrowUpRight className="w-3 h-3 text-gray-300 dark:text-gray-600" />
            </div>
            <div className="divide-y divide-gray-50 dark:divide-white/[0.04]">
              {VIP_CLIENTS.map((c, i) => (
                <div key={c.name} className="flex items-center gap-2 px-4 py-1.5 hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors cursor-pointer group">
                  <div className="relative flex-shrink-0">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-black ${D_AVATAR[i % D_AVATAR.length]}`}>{c.ini}</div>
                    <img src={`https://flagcdn.com/w20/${c.cc}.png`} width="8" alt={c.cc} className="absolute -bottom-0.5 -right-0.5 rounded-[1.5px] shadow-[0_0_0_1px_white] dark:shadow-[0_0_0_1px_#0d0d1a]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[9px] font-bold text-gray-700 dark:text-gray-200 truncate group-hover:text-gray-900 dark:group-hover:text-white transition-colors">{c.name}</div>
                    <div className="text-[8px] text-gray-400 truncate">{c.email}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-[9px] font-black text-emerald-500 tabular-nums">{c.value}</div>
                    <div className={`text-[7px] font-black uppercase px-1 rounded-md inline-block ${c.segCls}`}>{c.seg}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white dark:bg-[#0d0d1a] flex flex-col">
            <div className="flex items-center justify-between px-4 py-1.5 border-b border-gray-100 dark:border-white/[0.07]">
              <span className="text-[10px] font-black text-gray-700 dark:text-gray-200">Top 5 Produtos</span>
              <ArrowUpRight className="w-3 h-3 text-gray-300 dark:text-gray-600" />
            </div>
            <div className="divide-y divide-gray-50 dark:divide-white/[0.04]">
              {TOP_PRODS.map((p, i) => (
                <div key={p.rank} className="flex items-center gap-2 px-4 py-1.5 hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors cursor-pointer group">
                  <span className="text-[9px] font-black text-gray-300 dark:text-gray-600 w-3 flex-shrink-0">{p.rank}</span>
                  <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 ${D_AVATAR[i % D_AVATAR.length]}`}>
                    <Package className="w-3 h-3" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[9px] font-bold text-gray-700 dark:text-gray-200 truncate leading-tight group-hover:text-gray-900 dark:group-hover:text-white transition-colors">{p.name}</div>
                    {p.badge && <span className="text-[7px] font-black text-emerald-600 bg-emerald-500/10 px-1 rounded-md uppercase inline-block">{p.badge}</span>}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-[9px] font-black text-gray-700 dark:text-gray-200 tabular-nums group-hover:text-gray-900 dark:group-hover:text-white transition-colors">{p.sales.toLocaleString("pt-BR")}</div>
                    <div className="text-[7px] text-gray-400">vendidos</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </BrowserShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Mock 1 · Logística / Pedidos (tabela fiel ao sistema real — usada no Hero)
// ─────────────────────────────────────────────────────────────────────────────

const PAY_MAP: Record<string, { icon: React.ElementType; label: string }> = {
  pix: { icon: QrCode, label: "Pix" },
  boleto: { icon: Banknote, label: "Boleto" },
  paypal: { icon: Globe, label: "PayPal" },
  card: { icon: CreditCard, label: "Cartão" },
};

const ORIGIN_CLS: Record<string, string> = {
  shopify: "bg-[#95BF47]/5 text-[#739634] dark:text-[#95BF47] border-[#95BF47]/20",
  yampi: "bg-[#4F26B5]/5 text-[#4F26B5] dark:text-[#8b6ae5] border-[#4F26B5]/20",
};

type OrderRow = {
  id: string;
  platform: "shopify" | "yampi";
  country: string;
  date: string;
  time: string;
  clientName: string;
  clientEmail: string;
  status: "pago" | "pendente" | "cancelado";
  pay: keyof typeof PAY_MAP;
  value: string;
  highlight: boolean;
};

const ORDERS: OrderRow[] = [
  { id: "#10042", platform: "shopify", country: "br", date: "14/04/26", time: "09:12", clientName: "Ana Lima",        clientEmail: "ana@gmail.com",       status: "pago",      pay: "pix",    value: "R$189,00",   highlight: false },
  { id: "#10041", platform: "shopify",  country: "mx", date: "13/04/26", time: "14:37", clientName: "Diego Ruiz",      clientEmail: "diego@correo.mx",     status: "pendente",  pay: "card",   value: "R$342,50",   highlight: false },
  { id: "#10040", platform: "shopify", country: "us", date: "13/04/26", time: "08:55", clientName: "Emily Carter",    clientEmail: "emily@outlook.com",   status: "pago",      pay: "paypal", value: "R$97,00",    highlight: false },
  { id: "#10039", platform: "yampi",   country: "br", date: "12/04/26", time: "17:01", clientName: "Fernanda Rocha",  clientEmail: "fe@loja.com",         status: "cancelado", pay: "pix",    value: "R$512,00",   highlight: true  },
  { id: "#10038", platform: "shopify", country: "gb", date: "11/04/26", time: "11:22", clientName: "James Walker",    clientEmail: "james@hotmail.co.uk", status: "pago",      pay: "card",   value: "R$221,00",   highlight: false },
  { id: "#10037", platform: "shopify", country: "es", date: "11/04/26", time: "09:04", clientName: "Laura Martínez",  clientEmail: "laura@gmail.es",      status: "pago",      pay: "paypal", value: "R$455,00",   highlight: false },
  { id: "#10036", platform: "yampi",   country: "br", date: "10/04/26", time: "16:30", clientName: "Camila Ferreira",  clientEmail: "camila@loja.com.br",  status: "pendente",  pay: "boleto", value: "R$178,00",   highlight: false },
  { id: "#10035", platform: "shopify", country: "us", date: "10/04/26", time: "12:15", clientName: "Sarah Johnson",   clientEmail: "sarahj@gmail.com",    status: "pago",      pay: "card",   value: "R$310,00",   highlight: false },
  { id: "#10034", platform: "yampi",   country: "br", date: "09/04/26", time: "18:47", clientName: "Rafael Oliveira", clientEmail: "rafael@email.com",    status: "pago",      pay: "pix",    value: "R$89,00",    highlight: false },
];

function StatusBadge({ status }: { status: OrderRow["status"] }) {
  if (status === "pago") return (
    <div className="inline-flex items-center rounded-md border border-emerald-500/20 bg-emerald-500/10 text-emerald-600 text-[9px] px-2.5 py-1 gap-1.5 font-semibold uppercase tracking-[0.1em]">
      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)] animate-pulse" />
      Pago
    </div>
  );
  if (status === "pendente") return (
    <div className="inline-flex items-center rounded-md border border-amber-500/20 bg-amber-500/10 text-amber-600 text-[9px] px-2.5 py-1 gap-1.5 font-semibold uppercase tracking-[0.1em]">
      <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
      Pend.
    </div>
  );
  return (
    <div className="inline-flex items-center rounded-md border border-red-500/20 bg-red-500/10 text-red-600 text-[9px] px-2.5 py-1 gap-1.5 font-semibold uppercase tracking-[0.1em]">
      <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
      Canc.
    </div>
  );
}

export function LogisticsMock() {
  return (
    <BrowserShell url="mulply.com/orders">
      <div className="bg-white dark:bg-[#0d0d1a] flex flex-col">
        <div className="flex items-center justify-between px-5 py-2.5 border-b border-gray-100 dark:border-white/[0.07] bg-gray-50/40 dark:bg-white/[0.02]">
          <div className="flex items-center gap-4">
            <MulplyLogo />
            <div className="hidden sm:flex items-center gap-2">
              <Package className="w-3.5 h-3.5 text-[#48c1ce]" />
              <span className="text-xs font-black text-gray-700 dark:text-gray-100">Pedidos</span>
              <span className="px-2 py-0.5 rounded-full bg-[#48c1ce]/10 text-[#48c1ce] text-[9px] font-black">142 hoje</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {(["Filtro", "Sync", "CSV"] as const).map((label, i) => (
              <button key={label} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-white/15 text-[9px] font-bold text-gray-500 dark:text-gray-400 bg-transparent">
                {i === 0 && <Filter className="w-2.5 h-2.5" />}
                {i === 1 && <RefreshCw className="w-2.5 h-2.5" />}
                {i === 2 && <Download className="w-2.5 h-2.5" />}
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[680px] border-collapse">
            <thead>
              <tr className="border-b border-gray-100 dark:border-white/[0.07] bg-gray-50 dark:bg-white/[0.025]">
                {["PEDIDO", "ORIGEM", "DATA", "CLIENTE", "STATUS", "PAGAMENTO", "VALOR", ""].map((h) => (
                  <th key={h} className="px-4 py-2 text-left text-[9px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 whitespace-nowrap first:pl-5 last:pr-5">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ORDERS.map((row) => {
                const { icon: PayIcon, label: payLabel } = PAY_MAP[row.pay];
                return (
                  <tr
                    key={row.id}
                    className={`border-b last:border-0 transition-colors ${row.highlight
                      ? "border-red-200/60 dark:border-red-500/20 bg-red-50/50 dark:bg-red-500/[0.06]"
                      : "border-gray-50 dark:border-white/[0.04] hover:bg-gray-50/50 dark:hover:bg-white/[0.025]"
                      }`}
                    style={row.highlight ? { borderLeft: "2px solid #ef4444" } : {}}
                  >
                    <td className="px-4 py-3 pl-5">
                      <span className="font-semibold text-sm text-gray-800 dark:text-gray-100 tabular-nums">{row.id}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1.5 items-start">
                        <div className={`flex items-center gap-1.5 px-2 py-1 rounded-md font-semibold text-[9px] uppercase tracking-[0.1em] border ${ORIGIN_CLS[row.platform]}`}>
                          <ShoppingBag className="w-3 h-3 opacity-60" />
                          <span>{row.platform}</span>
                        </div>
                        <div className="flex items-center gap-1 pl-0.5">
                          <img
                            src={`https://flagcdn.com/w20/${row.country}.png`}
                            srcSet={`https://flagcdn.com/w40/${row.country}.png 2x`}
                            width="14"
                            alt={row.country.toUpperCase()}
                            className="rounded-[1.5px] shadow-[0_0_0_1px_rgba(0,0,0,0.1)] object-cover"
                          />
                          <span className="text-[9px] font-semibold tracking-[0.1em] text-gray-400 dark:text-gray-500">{row.country.toUpperCase()}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col">
                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 tabular-nums">{row.date}</span>
                        <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500 tabular-nums">{row.time}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-0.5">
                        <span className="font-semibold text-sm text-gray-800 dark:text-gray-100 leading-tight">{row.clientName}</span>
                        <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500 truncate max-w-[130px]">{row.clientEmail}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={row.status} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 text-xs font-bold text-gray-600 dark:text-gray-300">
                        <PayIcon className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        <span>{payLabel}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-sm text-emerald-600 dark:text-emerald-400 tabular-nums">{row.value}</span>
                    </td>
                    <td className="px-4 py-3 pr-5">
                      <button className="w-6 h-6 rounded-md bg-gray-100 dark:bg-white/10 flex items-center justify-center">
                        <MoreHorizontal className="w-3 h-3 text-gray-400" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-5 py-2.5 border-t border-gray-100 dark:border-white/[0.07] bg-gray-50/40 dark:bg-white/[0.02]">
          <span className="text-[9px] text-gray-400 dark:text-gray-500 font-semibold">9 de 142 pedidos · Sincronizado automaticamente</span>
          <span className="flex items-center gap-1 text-[#48c1ce] text-[9px] font-black cursor-pointer">Ver todos <ArrowUpRight className="w-3 h-3" /></span>
        </div>
      </div>
    </BrowserShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Mock 2 · CRM & Retenção (10 KPIs como cards, igual à página real)
// ─────────────────────────────────────────────────────────────────────────────

function HealthRing({ score }: { score: number }) {
  const r = 12;
  const circ = 2 * Math.PI * r;
  const color = score >= 70 ? "#10b981" : score >= 40 ? "#f59e0b" : "#ef4444";
  return (
    <div className="relative flex items-center justify-center w-9 h-9 flex-shrink-0">
      <svg width="36" height="36" viewBox="0 0 36 36" className="absolute">
        <circle cx="18" cy="18" r={r} fill="none" stroke="currentColor" strokeWidth="3" className="text-gray-200 dark:text-white/10" />
        <circle cx="18" cy="18" r={r} fill="none" stroke={color} strokeWidth="3"
          strokeDasharray={`${(score / 100) * circ} ${circ}`} strokeLinecap="round" transform="rotate(-90 18 18)" />
      </svg>
      <div className="relative flex flex-col items-center leading-none">
        <Heart className="w-2.5 h-2.5" style={{ color }} />
        <span className="text-[7px] font-black tabular-nums" style={{ color }}>{score}</span>
      </div>
    </div>
  );
}

type SegmentKey = "vip_ativo" | "promissor" | "vip_risco" | "perdido" | "adormecido" | "novo";

const SEGMENT_CFG: Record<SegmentKey, { label: string; icon: React.ElementType; cls: string }> = {
  vip_ativo: { label: "VIP ATIVO", icon: Trophy, cls: "text-amber-600 bg-amber-500/10 border-amber-500/20" },
  vip_risco: { label: "VIP EM RISCO", icon: AlertTriangle, cls: "text-orange-600 bg-orange-500/10 border-orange-500/20" },
  promissor: { label: "PROMISSOR", icon: TrendingUp, cls: "text-emerald-600 bg-emerald-500/10 border-emerald-500/20" },
  novo: { label: "NOVO", icon: Sparkles, cls: "text-blue-600 bg-blue-500/10 border-blue-500/20" },
  adormecido: { label: "ADORMECIDO", icon: Moon, cls: "text-gray-500 bg-gray-500/10 border-gray-500/20" },
  perdido: { label: "PERDIDO", icon: XCircle, cls: "text-red-600 bg-red-500/10 border-red-500/20" },
};

function SegmentBadge({ seg }: { seg: SegmentKey }) {
  const { label, icon: Icon, cls } = SEGMENT_CFG[seg];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-lg font-black uppercase tracking-wider border px-2 py-1 text-[9px] whitespace-nowrap ${cls}`}>
      <Icon className="w-2.5 h-2.5 flex-shrink-0" />
      {label}
    </span>
  );
}

const CRM_CLIENTS: {
  name: string; initials: string; email: string; avatarCls: string;
  seg: SegmentKey; score: number; orders: number; ltv: string; lastBuy: string; canal: string; risk: boolean;
}[] = [
    { name: "Ana Lima", initials: "AL", email: "ana@gmail.com", avatarCls: "bg-[#48c1ce]/20 text-[#48c1ce]", seg: "vip_ativo", score: 92, orders: 14, ltv: "R$2.647", lastBuy: "há 3 dias", canal: "WhatsApp", risk: false },
    { name: "Carlos Mendes", initials: "CM", email: "carlos@email.com", avatarCls: "bg-emerald-500/20 text-emerald-600", seg: "promissor", score: 68, orders: 6, ltv: "R$892", lastBuy: "há 12 dias", canal: "Instagram", risk: false },
    { name: "Julia Souza", initials: "JS", email: "julia@gmail.com", avatarCls: "bg-blue-500/20 text-blue-600", seg: "novo", score: 45, orders: 2, ltv: "R$189", lastBuy: "há 2 dias", canal: "E-mail", risk: false },
    { name: "Fernanda Rocha", initials: "FR", email: "fe@loja.com", avatarCls: "bg-orange-500/20 text-orange-600", seg: "vip_risco", score: 31, orders: 21, ltv: "R$5.120", lastBuy: "há 48 dias", canal: "WhatsApp", risk: true },
    { name: "Paulo Torres", initials: "PT", email: "paulo@gmail.com", avatarCls: "bg-gray-400/20 text-gray-500", seg: "adormecido", score: 18, orders: 3, ltv: "R$441", lastBuy: "há 90 dias", canal: "E-mail", risk: true },
  ];

const SEG_COUNTS: Record<SegmentKey, string> = {
  vip_ativo: "25", vip_risco: "66", promissor: "561", novo: "8.775", adormecido: "155", perdido: "27",
};

function CRMMock() {
  return (
    <BrowserShell url="mulply.com/customers">
      <div className="bg-white dark:bg-[#0d0d1a] flex flex-col">
        {/* App header */}
        <div className="flex items-center justify-between px-5 py-2.5 border-b border-gray-100 dark:border-white/[0.07] bg-gray-50/40 dark:bg-white/[0.02]">
          <div className="flex items-center gap-3">
            <MulplyLogo />
            <div className="flex items-center gap-2">
              <Users className="w-3.5 h-3.5 text-[#ec399a]" />
              <span className="text-xs font-black text-gray-700 dark:text-gray-100">Clientes & Retenção</span>
            </div>
          </div>
          <div className="relative">
            <Search className="w-3 h-3 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <div className="pl-7 pr-3 py-1.5 rounded-lg bg-gray-100 dark:bg-white/10 text-[10px] text-gray-400 font-medium w-36 select-none">Buscar cliente...</div>
          </div>
        </div>

        {/* Row 1: 4 KPI cards (grid-cols-4) — igual à página real */}
        <div className="grid grid-cols-4 gap-px bg-gray-100/80 dark:bg-white/[0.05] border-b border-gray-100 dark:border-white/[0.07]">
          {[
            { icon: Users, label: "TOTAL DE CLIENTES", value: "9.952", valueCls: "text-blue-600 dark:text-blue-400", alert: false },
            { icon: UserCheck, label: "CLIENTES ATIVOS", value: "3.964", valueCls: "text-emerald-600 dark:text-emerald-400", alert: false },
            { icon: TrendingUp, label: "LTV MÉDIO", value: "R$189,25", valueCls: "text-amber-600 dark:text-amber-400", alert: false },
            { icon: AlertTriangle, label: "EM RISCO DE CHURN", value: "3.881", valueCls: "text-red-600 dark:text-red-400", alert: true },
          ].map(({ icon: Icon, label, value, valueCls, alert }) => (
            <div key={label} className={`relative flex flex-col gap-1.5 px-4 py-3 bg-white dark:bg-[#0d0d1a] overflow-hidden ${alert ? "border border-red-500/20" : ""}`}>
              {alert && <div className="absolute inset-0 bg-red-500/5 dark:bg-red-500/[0.06] pointer-events-none" />}
              <div className="flex items-center justify-between">
                <span className="text-[8px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">{label}</span>
                <Icon className={`w-3 h-3 ${valueCls}`} />
              </div>
              <span className={`text-xl font-black tabular-nums leading-none ${valueCls}`}>{value}</span>
            </div>
          ))}
        </div>

        {/* Row 2: 6 segment cards (grid-cols-6) — igual à página real */}
        <div className="grid grid-cols-6 gap-px bg-gray-100/60 dark:bg-white/[0.04] border-b border-gray-100 dark:border-white/[0.07]">
          {(Object.entries(SEGMENT_CFG) as [SegmentKey, typeof SEGMENT_CFG[SegmentKey]][]).map(([key, cfg]) => {
            const Icon = cfg.icon;
            const colorMap: Record<SegmentKey, string> = {
              vip_ativo: "text-amber-500", vip_risco: "text-orange-500",
              promissor: "text-emerald-500", novo: "text-blue-500",
              adormecido: "text-slate-500", perdido: "text-red-500",
            };
            return (
              <div key={key} className="bg-white dark:bg-[#0d0d1a] flex flex-col items-center gap-1 py-3 px-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors">
                <div className="flex items-center gap-1">
                  <Icon className={`w-2.5 h-2.5 ${colorMap[key]}`} />
                  <span className={`text-lg font-black tabular-nums leading-none ${colorMap[key]}`}>{SEG_COUNTS[key]}</span>
                </div>
                <span className="text-[7px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide text-center leading-tight">{cfg.label}</span>
              </div>
            );
          })}
        </div>

        {/* Customer Table */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse">
            <thead>
              <tr className="border-b border-gray-100 dark:border-white/[0.07] bg-gray-50 dark:bg-white/[0.025]">
                {["CLIENTE", "SEGMENTO", "HEALTH SCORE", "PEDIDOS", "LTV", "ÚLTIMA COMPRA", "CANAL", "AÇÕES"].map((h) => (
                  <th key={h} className="px-4 py-2 text-left text-[9px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 whitespace-nowrap first:pl-5 last:pr-5">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CRM_CLIENTS.map((c) => (
                <tr key={c.name} className={`border-b last:border-0 transition-colors ${c.risk ? "border-red-100/80 dark:border-red-500/[0.12] bg-red-50/40 dark:bg-red-500/[0.04]" : "border-gray-50 dark:border-white/[0.04] hover:bg-gray-50/50 dark:hover:bg-white/[0.025]"}`}>
                  <td className="px-4 py-3 pl-5">
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-xl flex items-center justify-center text-[9px] font-black flex-shrink-0 ${c.avatarCls}`}>{c.initials}</div>
                      <div className="flex flex-col gap-0.5">
                        <span className="font-semibold text-sm text-gray-800 dark:text-gray-100 leading-tight whitespace-nowrap">{c.name}</span>
                        <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500 truncate max-w-[120px]">{c.email}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3"><SegmentBadge seg={c.seg} /></td>
                  <td className="px-4 py-3"><HealthRing score={c.score} /></td>
                  <td className="px-4 py-3"><span className="text-xs font-semibold text-gray-700 dark:text-gray-200 tabular-nums">{c.orders}</span></td>
                  <td className="px-4 py-3"><span className={`text-xs font-black tabular-nums ${c.risk ? "text-red-500 dark:text-red-400" : "text-gray-800 dark:text-gray-100"}`}>{c.ltv}</span></td>
                  <td className="px-4 py-3"><span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 whitespace-nowrap">{c.lastBuy}</span></td>
                  <td className="px-4 py-3"><span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400">{c.canal}</span></td>
                  <td className="px-4 py-3 pr-5">
                    <div className="flex items-center gap-1.5">
                      <button className="w-6 h-6 rounded-md bg-gray-100 dark:bg-white/10 flex items-center justify-center group">
                        <Eye className="w-3 h-3 text-gray-400 group-hover:text-[#48c1ce]" />
                      </button>
                      <button className="w-6 h-6 rounded-md bg-gray-100 dark:bg-white/10 flex items-center justify-center">
                        <MoreHorizontal className="w-3 h-3 text-gray-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-5 py-2.5 border-t border-gray-100 dark:border-white/[0.07] bg-gray-50/40 dark:bg-white/[0.02]">
          <span className="text-[9px] text-gray-400 dark:text-gray-500 font-semibold">5 de 9.952 clientes · Segmentação atualizada automaticamente</span>
          <span className="flex items-center gap-1 text-[#ec399a] text-[9px] font-black cursor-pointer">Ver relatório <ArrowUpRight className="w-3 h-3" /></span>
        </div>
      </div>
    </BrowserShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Mock 3 · Helpdesk Omnichannel
// ─────────────────────────────────────────────────────────────────────────────

const CONVOS = [
  { name: "Talita P.", preview: "Cadê meu pedido? Faz 5 dias...", time: "14:32", unread: 2, ch: "whatsapp", chColor: "#25D366", active: true, status: "Aguardando" },
  { name: "Manuel M.", preview: "Quando vai sair para entrega?", time: "13:10", unread: 0, ch: "instagram", chColor: "#ec399a", active: false, status: "Aberto" },
  { name: "Claudia T.", preview: "Quero cancelar o meu pedido", time: "11:45", unread: 1, ch: "email", chColor: "#48c1ce", active: false, status: "SLA Risco" },
];
const CH_ICONS: Record<string, React.ElementType> = { whatsapp: MessageCircle, instagram: Camera, email: Mail };

function HelpdeskMock() {
  return (
    <BrowserShell url="mulply.com/atendimento">
      <div className="bg-white dark:bg-[#0d0d1a] flex" style={{ height: "316px" }}>
        {/* Sidebar */}
        <div className="w-44 flex-shrink-0 border-r border-gray-100 dark:border-white/[0.07] flex flex-col">
          <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100 dark:border-white/[0.07]">
            <MulplyLogo />
          </div>
          {/* Status tabs */}
          <div className="flex border-b border-gray-100 dark:border-white/[0.07]">
            {[["Abertos", "4", true], ["Aguardando", "3", false], ["Resolvidos", "5", false]].map(([label, count, active]) => (
              <button key={label as string} className={`flex-1 py-1.5 text-[8px] font-black relative ${active ? "text-[#48c1ce]" : "text-gray-400 dark:text-gray-500"}`}>
                {label}
                <span className={`ml-0.5 text-[7px] ${active ? "text-[#48c1ce]" : "text-gray-400/60"}`}>({count})</span>
                {active && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#48c1ce]" />}
              </button>
            ))}
          </div>
          {/* Search */}
          <div className="px-2 py-2 border-b border-gray-100 dark:border-white/[0.07]">
            <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-gray-100 dark:bg-white/10">
              <Search className="w-2.5 h-2.5 text-gray-400 flex-shrink-0" />
              <span className="text-[9px] text-gray-400 select-none">Buscar conversa...</span>
            </div>
          </div>
          {/* Conversations */}
          <div className="flex-1 overflow-hidden divide-y divide-gray-50 dark:divide-white/[0.04]">
            {CONVOS.map((c) => {
              const Icon = CH_ICONS[c.ch];
              const statusCls = c.status === "SLA Risco"
                ? "bg-red-500/10 text-red-500 border-red-500/20"
                : c.status === "Aguardando"
                  ? "bg-amber-500/10 text-amber-600 border-amber-500/20"
                  : "bg-emerald-500/10 text-emerald-600 border-emerald-500/20";
              return (
                <div key={c.name} className={`px-3 py-2.5 cursor-pointer ${c.active ? "bg-[#48c1ce]/10 border-l-2 border-l-[#48c1ce]" : "hover:bg-gray-50 dark:hover:bg-white/[0.03]"}`}>
                  <div className="flex items-start justify-between gap-1 mb-1">
                    <span className={`text-[10px] font-black truncate ${c.active ? "text-[#48c1ce]" : "text-gray-700 dark:text-gray-200"}`}>{c.name}</span>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      <Icon className="w-2.5 h-2.5" style={{ color: c.chColor }} />
                      {c.unread > 0 && <span className="w-4 h-4 rounded-full bg-[#48c1ce] text-white text-[7px] font-black flex items-center justify-center">{c.unread}</span>}
                    </div>
                  </div>
                  <p className="text-[8px] text-gray-400 truncate mb-1">{c.preview}</p>
                  <div className="flex items-center justify-between">
                    <span className={`text-[7px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded border ${statusCls}`}>{c.status}</span>
                    <span className="text-[7px] text-gray-300 dark:text-gray-600">{c.time}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Chat area */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 dark:border-white/[0.07] bg-gray-50/50 dark:bg-white/[0.02]">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-amber-500/20 text-amber-600 text-[9px] font-black flex items-center justify-center">TP</div>
              <div>
                <div className="text-[10px] font-black text-gray-700 dark:text-gray-200">Talita P.</div>
                <div className="flex items-center gap-1">
                  <MessageCircle className="w-2 h-2" style={{ color: "#25D366" }} />
                  <span className="text-[8px] text-gray-400">WhatsApp · Online agora</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <Phone className="w-3 h-3 text-gray-400" />
              <MoreHorizontal className="w-3 h-3 text-gray-400" />
            </div>
          </div>
          <div className="flex-1 overflow-hidden p-3 space-y-2 bg-gray-50/30 dark:bg-white/[0.01]">
            {/* Context chip */}
            <div className="flex justify-center">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 dark:bg-white/10 text-[8px] font-semibold text-gray-500 dark:text-gray-400">
                <Package className="w-2.5 h-2.5 text-[#48c1ce]" />
                Pedido #43773 vinculado automaticamente
              </div>
            </div>
            <div className="flex justify-start">
              <div className="max-w-[76%] px-2.5 py-2 rounded-xl rounded-bl-sm bg-white dark:bg-white/10 border border-gray-100 dark:border-white/10 shadow-sm">
                <p className="text-[9px] font-medium text-gray-700 dark:text-gray-200 leading-relaxed">Cadê meu pedido? Faz 5 dias que não vejo nada no rastreio!</p>
                <span className="text-[7px] text-gray-400 mt-0.5 block">14:32</span>
              </div>
            </div>
            <div className="flex justify-end">
              <div className="max-w-[76%] px-2.5 py-2 rounded-xl rounded-br-sm bg-[#48c1ce] shadow-sm shadow-[#48c1ce]/20">
                <p className="text-[9px] font-medium text-white leading-relaxed">Olá Talita! Seu pedido #43773 está em SP. Previsão: amanhã até 20h ✓</p>
                <span className="text-[7px] text-white/70 mt-0.5 block">14:33</span>
              </div>
            </div>
            <div className="flex justify-start">
              <div className="max-w-[76%] px-2.5 py-2 rounded-xl rounded-bl-sm bg-white dark:bg-white/10 border border-gray-100 dark:border-white/10 shadow-sm">
                <p className="text-[9px] font-medium text-gray-700 dark:text-gray-200 leading-relaxed">Obrigada! Mas e o rastreio, por que não atualiza?</p>
                <span className="text-[7px] text-gray-400 mt-0.5 block">14:35</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-2 border-t border-gray-100 dark:border-white/[0.07]">
            <Paperclip className="w-3 h-3 text-gray-400 flex-shrink-0" />
            <div className="flex-1 h-7 rounded-xl bg-gray-100 dark:bg-white/10 px-2.5 flex items-center">
              <span className="text-[9px] text-gray-400 select-none">Responder Talita...</span>
            </div>
            <div className="w-7 h-7 rounded-xl bg-[#48c1ce] flex items-center justify-center flex-shrink-0">
              <Send className="w-3 h-3 text-white" />
            </div>
          </div>
        </div>

        {/* Context sidebar */}
        <div className="w-32 flex-shrink-0 border-l border-gray-100 dark:border-white/[0.07] flex flex-col bg-gray-50/50 dark:bg-white/[0.02]">
          <div className="px-2.5 py-2.5 border-b border-gray-100 dark:border-white/[0.07]">
            <span className="text-[8px] font-black uppercase tracking-wider text-gray-400">Dados do Cliente</span>
          </div>
          <div className="flex-1 p-2.5 space-y-2.5 overflow-hidden">
            <div className="p-2 rounded-xl bg-white dark:bg-white/10 border border-gray-200/50 dark:border-white/10 shadow-sm">
              <div className="flex items-center gap-1 mb-1.5">
                <Package className="w-2.5 h-2.5 text-[#48c1ce]" />
                <span className="text-[8px] font-black text-[#48c1ce]">#43773</span>
              </div>
              <div className="space-y-1.5">
                <div>
                  <div className="text-[7px] text-gray-400 uppercase mb-0.5">Origem</div>
                  <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[7px] font-black border ${ORIGIN_CLS.yampi}`}>
                    <ShoppingBag className="w-2 h-2 opacity-60" />yampi
                  </div>
                </div>
                <div>
                  <div className="text-[7px] text-gray-400 uppercase mb-0.5">Status</div>
                  <div className="inline-flex items-center rounded-md border border-amber-500/20 bg-amber-500/10 text-amber-600 text-[7px] px-1.5 py-0.5 gap-1 font-semibold uppercase">
                    <div className="w-1 h-1 rounded-full bg-amber-500" />
                    Pend.
                  </div>
                </div>
                <div>
                  <div className="text-[7px] text-gray-400 uppercase mb-0.5">Valor</div>
                  <span className="text-[8px] font-black text-emerald-600">R$219,23</span>
                </div>
              </div>
            </div>
            <div className="p-2 rounded-xl bg-white dark:bg-white/10 border border-gray-200/50 dark:border-white/10 shadow-sm">
              <div className="text-[7px] text-gray-400 uppercase mb-1.5">Histórico</div>
              <div className="space-y-1.5">
                <div><div className="text-[7px] text-gray-400">Pedidos</div><span className="text-[8px] font-black text-gray-700 dark:text-gray-200">9 pedidos</span></div>
                <div><div className="text-[7px] text-gray-400">LTV</div><span className="text-[8px] font-black text-[#ec399a]">R$1.764,12</span></div>
                <div><div className="text-[7px] text-gray-400">Segmento</div>
                  <span className="inline-flex items-center gap-0.5 text-[7px] font-black text-orange-600">
                    <AlertTriangle className="w-2 h-2" />VIP EM RISCO
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </BrowserShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Layout blocks
// ─────────────────────────────────────────────────────────────────────────────

type FeatureBaseProps = {
  eyebrow: string; title: React.ReactNode; description: string;
  bullets: string[]; mock: React.ReactNode; accentColor: string;
};

function WideFeature({ eyebrow, title, description, bullets, mock, accentColor }: FeatureBaseProps) {
  return (
    <div className="flex flex-col gap-10">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true as const }}
        transition={{ duration: 0.5, ease: "easeOut" as const }}
        className="max-w-3xl mx-auto text-center flex flex-col items-center gap-4"
      >
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold"
          style={{ background: `${accentColor}18`, color: accentColor, border: `1px solid ${accentColor}35` }}>
          <Zap className="w-3 h-3" />
          {eyebrow}
        </span>
        <h3 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-black tracking-tight text-gray-900 dark:text-white leading-[1.08]">{title}</h3>
        <p className="text-base text-gray-500 dark:text-gray-400 leading-relaxed max-w-2xl">{description}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left mt-2 w-full">
          {bullets.map((b) => (
            <div key={b} className="flex items-start gap-2.5 text-sm font-semibold text-gray-700 dark:text-gray-200">
              <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: accentColor }} />
              {b}
            </div>
          ))}
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true as const }}
        transition={{ duration: 0.65, ease: "easeOut" as const }}
        className="w-full relative"
      >
        <div className="absolute -inset-10 rounded-3xl blur-3xl opacity-[0.12] pointer-events-none"
          style={{ background: `radial-gradient(ellipse at center, ${accentColor} 0%, transparent 70%)` }} />
        <div className="relative">{mock}</div>
      </motion.div>
    </div>
  );
}

function SideFeature({ eyebrow, title, description, bullets, mock, accentColor, reverse }: FeatureBaseProps & { reverse?: boolean }) {
  return (
    <div className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start ${reverse ? "lg:[direction:rtl]" : ""}`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true as const }}
        transition={{ duration: 0.5, ease: "easeOut" as const }}
        className="lg:col-span-5 flex flex-col gap-4 lg:[direction:ltr]"
      >
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold w-fit"
          style={{ background: `${accentColor}18`, color: accentColor, border: `1px solid ${accentColor}35` }}>
          <Zap className="w-3 h-3" />
          {eyebrow}
        </span>
        <h3 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-gray-900 dark:text-white leading-tight">{title}</h3>
        <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 leading-relaxed">{description}</p>
        <ul className="flex flex-col gap-2.5 mt-1">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-2.5 text-sm font-semibold text-gray-700 dark:text-gray-200">
              <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: accentColor }} />
              {b}
            </li>
          ))}
        </ul>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, x: reverse ? -24 : 24 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true as const }}
        transition={{ duration: 0.65, ease: "easeOut" as const }}
        className="lg:col-span-7 lg:[direction:ltr] relative"
      >
        <div className="absolute -inset-6 rounded-3xl blur-3xl opacity-15 pointer-events-none"
          style={{ background: `radial-gradient(ellipse, ${accentColor}, transparent 70%)` }} />
        <div className="relative">{mock}</div>
      </motion.div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Section — sem header redundante (cada feature tem seu próprio título)
// ─────────────────────────────────────────────────────────────────────────────

export function FeaturesLP3() {
  return (
    <section id="features" className="py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="w-full max-w-7xl mx-auto flex flex-col gap-28">

        <WideFeature
          eyebrow="Rastreio & Logística"
          title={<>Monitore SLA, rastreio e exceções{" "}<span className="bg-gradient-to-r from-primary via-purple-600 to-pink-600 bg-clip-text text-transparent">em tempo real.</span></>}
          description="Chega de abrir cada pedido no site da transportadora. O Mulply centraliza o status logístico de todos os seus pedidos, detecta SLA violado antes do cliente reclamar e permite intervenção manual via SAC."
          bullets={[
            "Status logístico por pedido: Em Trânsito, Fiscal., Taxado, Entregue",
            "Coluna \"Sem movimento\" com alerta em âmbar (≥7d) e vermelho (≥15d)",
            "Override manual de status via painel de SAC",
            "Exportação CSV com filtros por status e período",
          ]}
          mock={<TrackingMock />}
          accentColor="#48c1ce"
        />

        <WideFeature
          eyebrow="CRM & Retenção"
          title={<>Conheça seus clientes de verdade.{" "}<span className="bg-gradient-to-r from-primary via-purple-600 to-pink-600 bg-clip-text text-transparent">Segmente. Retenha.</span></>}
          description="Identifique quem está prestes a abandonar sua loja e aja antes. O Mulply calcula LTV, agrupa clientes por comportamento e mostra um health score claro."
          bullets={[
            "Segmentação RFM automática (VIP, em risco, dormentes)",
            "Health Score visual para cada cliente",
            "LTV médio e histórico de pedidos por pessoa",
            "Filtros por canal de aquisição e frequência de compra",
          ]}
          mock={<CRMMock />}
          accentColor="#10b981"
        />

        <SideFeature
          eyebrow="Helpdesk Unificado"
          title={<>WhatsApp, Instagram e e-mail —{" "}<span className="bg-gradient-to-r from-[#ec399a] to-pink-400 bg-clip-text text-transparent">tudo em um único inbox.</span></>}
          description="Sua equipe responde sem trocar de aba. O Mulply unifica todos os canais de atendimento, vincula automaticamente cada conversa ao pedido do cliente e avisa quando um SLA está vencendo."
          bullets={[
            "Inbox único para WhatsApp, Instagram DM e e-mail",
            "Cada ticket vinculado ao pedido e histórico do cliente",
            "Alertas de SLA com segmento e LTV do cliente visíveis",
            "Notas internas para colaboração entre agentes",
          ]}
          mock={<HelpdeskMock />}
          reverse
          accentColor="#ec399a"
        />

      </div>
    </section>
  );
}
